/**
 * CycleFilter Component
 * Multi-select filter for OKR cycles
 */

import React from 'react';
import { Calendar, Check } from 'lucide-react';

interface Cycle {
  id: string;
  name: string;
  type: string;
  status: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

interface CycleFilterProps {
  availableCycles: Cycle[];
  selectedCycleIds: string[];
  onChange: (cycleIds: string[]) => void;
}

const CYCLE_TYPE_LABELS: Record<string, string> = {
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  annual: 'Annual',
  custom: 'Custom',
};

const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const yearOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

  // If same year, don't repeat year
  if (start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', yearOptions)}`;
  }
  return `${start.toLocaleDateString('en-US', yearOptions)} - ${end.toLocaleDateString('en-US', yearOptions)}`;
};

export const CycleFilter: React.FC<CycleFilterProps> = ({
  availableCycles,
  selectedCycleIds,
  onChange,
}) => {
  const handleToggle = (cycleId: string) => {
    if (selectedCycleIds.includes(cycleId)) {
      onChange(selectedCycleIds.filter((id) => id !== cycleId));
    } else {
      onChange([...selectedCycleIds, cycleId]);
    }
  };

  if (availableCycles.length === 0) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-stone-700">
          Cycle
        </label>
        <p className="text-sm text-stone-500 italic">
          No cycles available. Create cycles in Admin Settings.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-stone-700">
        Cycle
      </label>
      <div className="space-y-2">
        {availableCycles.map((cycle) => {
          const isSelected = selectedCycleIds.includes(cycle.id);
          const typeLabel = CYCLE_TYPE_LABELS[cycle.type] || cycle.type;

          return (
            <button
              key={cycle.id}
              onClick={() => handleToggle(cycle.id)}
              className={`w-full flex items-start gap-3 px-3 py-2.5 border-2 rounded-lg transition-all text-left ${
                isSelected
                  ? 'bg-primary-50 border-primary-400 shadow-sm'
                  : 'bg-white border-stone-200 hover:bg-stone-50 hover:border-stone-300'
              }`}
            >
              {/* Checkbox indicator */}
              <div
                className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isSelected
                    ? 'bg-primary-600 border-primary-600'
                    : 'border-stone-300'
                }`}
              >
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>

              {/* Cycle info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-stone-900'}`}>
                    {cycle.name}
                  </span>
                  {cycle.isActive && (
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-success-100 text-success-700 rounded">
                      Active
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <Calendar className="w-3.5 h-3.5 text-stone-400" />
                  <span className="text-xs text-stone-500">
                    {formatDateRange(cycle.startDate, cycle.endDate)}
                  </span>
                  <span className="text-xs text-stone-400">•</span>
                  <span className="text-xs text-stone-500">{typeLabel}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
